<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto px-4">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-6">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-4 self-start">
            <v-icon small color="inherit">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-black uppercase tracking-widest">Voltar</span>
          </button>
          <h1 class="sci-page-header-title text-2xl font-black text-slate-800 uppercase tracking-tight">
            {{ id ? 'Editar Ordem de Serviço' : 'Gerar Ordem de Serviço' }}
          </h1>
          <p class="text-xs text-slate-500 font-bold mt-1 uppercase tracking-wider opacity-60">Gestão de Peças e Serviços</p>
        </div>
      </div>

      <v-form ref="form" v-model="valid" lazy-validation class="space-y-8 pb-12">
        
        <!-- SEÇÃO 1: IDENTIFICAÇÃO -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
          <div class="px-8 py-5 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-file-document-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO DA ORDEM DE SERVIÇO</h2>
          </div>
          
          <div class="p-10">
            <v-row>
              <!-- PREFEITURA -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Prefeitura</label>
                <v-autocomplete
                  v-model="data.prefecture_id"
                  :items="prefectures"
                  item-value="prefecture_id"
                  item-text="prefecture_social_name"
                  @change="[loadProviders(), getVehiclesByPrefecture()]"
                  :readonly="formDisable"
                  placeholder="SELECIONE A PREFEITURA"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- FORNECEDOR -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fornecedor</label>
                <v-autocomplete
                  v-model="data.provider_id"
                  :items="providers"
                  item-value="provider_id"
                  item-text="provider_name"
                  @change="[loadProvidersItem()]"
                  placeholder="SELECIONE O FORNECEDOR"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- VEÍCULO -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo</label>
                <v-autocomplete
                  v-model="data.vehicle_id"
                  :items="vehicles"
                  item-value="vehicle_id"
                  placeholder="SELECIONE O VEÍCULO"
                  solo flat dense hide-details
                  class="custom-select"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                  </template>
                  <template v-slot:item="{ item }">
                    {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- NÚMERO OS -->
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Número OS</label>
                <v-text-field
                  v-model="os_number_display"
                  readonly
                  placeholder="SEQUENCIAL AUTOMÁTICO"
                  solo flat dense hide-details
                  class="custom-select-readonly"
                />
              </v-col>

              <!-- DATA OS -->
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data da OS</label>
                <v-text-field
                  v-model="data.os_date"
                  v-mask-date.br
                  placeholder="DD/MM/AAAA"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- RESPONSÁVEL -->
              <v-col cols="12" sm="12" md="8">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Responsável</label>
                <v-text-field
                  v-model="data.os_responsible"
                  placeholder="DIGITE O NOME DO RESPONSÁVEL"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- STATUS -->
              <v-col cols="12" sm="12" md="4">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Status</label>
                <v-select
                  v-model="data.os_status"
                  :items="['ABERTO', 'EM ANDAMENTO', 'CONCLUIDO', 'CANCELADO']"
                  placeholder="SELECIONE O STATUS"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>
            </v-row>
          </div>
        </section>

        <!-- SEÇÃO 2: ITENS E SERVIÇOS -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
          <div class="px-8 py-5 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-format-list-checks</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ITENS E SERVIÇOS</h2>
            </div>
            <div class="flex gap-4">
              <button 
                type="button" 
                @click="addFieldParts"
                class="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4 py-2.5 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-orange-500/20"
              >
                <v-icon small color="white">mdi-plus</v-icon>
                ADD PEÇA
              </button>
              <button 
                type="button" 
                @click="addFieldServices"
                class="bg-slate-700 hover:bg-slate-800 text-white rounded-xl px-4 py-2.5 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-slate-700/20"
              >
                <v-icon small color="white">mdi-hammer-wrench</v-icon>
                ADD SERVIÇO
              </button>
            </div>
          </div>
          
          <div class="p-8">
            <div v-if="itens.length === 0" class="py-20 border-2 border-dashed border-slate-100 rounded-2xl flex flex-col items-center justify-center text-slate-300 bg-slate-50/50">
              <v-icon size="64" color="slate-200">mdi-playlist-plus</v-icon>
              <p class="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Nenhum item ou serviço adicionado</p>
            </div>

            <div class="space-y-4">
              <div v-for="(item, i) in itens" :key="i" class="p-6 bg-slate-50/80 rounded-2xl border border-slate-200 transition-all hover:bg-white hover:shadow-lg hover:shadow-slate-200/50 relative group">
                <v-row align="center" no-gutters v-if="item.isServico">
                  <v-col cols="12" md="8" class="pr-4">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Descrição do Serviço</label>
                    <v-text-field
                      v-model="item.servico"
                      placeholder="DESCREVA O SERVIÇO REALIZADO"
                      solo flat dense hide-details
                      class="custom-select-small uppercase"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Preço do Serviço</label>
                    <v-text-field
                      v-model="item.preco"
                      v-mask-decimal.br="2"
                      @change="onChangeTotal()"
                      placeholder="0,00"
                      prefix="R$"
                      solo flat dense hide-details
                      class="custom-select-small"
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="mt-4 md:mt-0 flex justify-end items-end pt-5">
                    <v-btn icon color="rose" @click="removeField(i)" class="bg-rose-50 hover:bg-rose-100 transition-colors">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row align="center" no-gutters v-else>
                  <v-col cols="12" md="4" class="pr-4">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Peça / Produto</label>
                    <v-autocomplete
                      v-model="item.peca_id"
                      :items="providersItem"
                      item-value="item_id"
                      placeholder="BUSCAR PEÇA"
                      solo flat dense hide-details
                      class="custom-select-small uppercase"
                    >
                      <template v-slot:item="{ item }">
                        <span class="text-[10px] font-black uppercase tracking-tight">{{ `${item.name} - ${item.marca} - ${item.modelo}` }}</span>
                      </template>
                      <template v-slot:selection="{ item }">
                        <span class="text-[10px] font-black uppercase tracking-tight">{{ `${item.name} - ${item.marca}` }}</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="2" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">P. Unitário</label>
                    <v-text-field
                      v-model="item.preco"
                      v-mask-decimal.br="2"
                      @change="onChangeParts(i)"
                      placeholder="0,00"
                      prefix="R$"
                      solo flat dense hide-details
                      class="custom-select-small"
                    />
                  </v-col>
                  <v-col cols="12" md="2" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Quantidade</label>
                    <v-text-field
                      v-model="item.quantidade"
                      v-mask-number
                      @change="[onChangeParts(i), onChangeTotal()]"
                      placeholder="0"
                      solo flat dense hide-details
                      class="custom-select-small text-center"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Total do Item</label>
                    <v-text-field
                      v-model="item.total"
                      v-mask-decimal.br="2"
                      readonly
                      placeholder="0,00"
                      prefix="R$"
                      solo flat dense hide-details
                      class="custom-select-small-readonly"
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="mt-4 md:mt-0 flex justify-end items-end pt-5">
                    <v-btn icon color="rose" @click="removeField(i)" class="bg-rose-50 hover:bg-rose-100 transition-colors">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </div>

            <!-- RESUMO E AÇÕES -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12">
              <div></div>
              <div class="p-8 bg-orange-50 rounded-2xl border border-orange-100 shadow-inner">
                <label class="text-[11px] font-black text-orange-400 uppercase tracking-widest mb-4 block">Valor Total da Ordem</label>
                <v-text-field
                  v-model="data.os_total"
                  v-mask-decimal.br="2"
                  readonly
                  placeholder="0,00"
                  prefix="R$"
                  outlined dense hide-details
                  class="bg-white rounded-xl shadow-lg font-black text-3xl text-orange-600"
                />
              </div>
            </div>

            <!-- FOOTER -->
            <div class="mt-12 bg-slate-50 px-8 py-4 border-t border-slate-100 flex justify-end items-center gap-4 -mx-8 -mb-8">
              <button 
                type="button"
                @click="$router.push('/parts-and-services/os')"
                style="background: none; border: none; cursor: pointer; white-space: nowrap;"
                class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
              >
                CANCELAR
              </button>
              <button
                type="button"
                @click="validate"
                :disabled="loader"
                style="background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 8px; border-radius: 8px; padding: 0 24px; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: none; cursor: pointer; height: 42px; width: auto; flex-shrink: 0;"
              >
                <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else small color="white">mdi-check</v-icon>
                <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR ORDEM' }}</span>
              </button>
            </div>
          </div>
        </section>
      </v-form>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="4000"
        rounded="xl"
        elevation="24"
        class="mb-8"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-black uppercase tracking-widest">{{ snack.message }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn dark icon v-bind="attrs" @click="snack.show = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    prefectures: [],
    providers: [],
    vehicles: [],
    providersItem: [],
    itens: [],
    data: {
      prefecture_id: null,
      provider_id: null,
      vehicle_id: null,
      os_number: null,
      os_date: "",
      os_responsible: "",
      os_status: "ABERTO",
      os_total: "0,00",
    },
    snack: {
      show: false,
      message: "",
      color: "",
    },
  }),
  computed: {
    formDisable() {
      return this.$store.getters.getUserData.table_key === "prefectures";
    },
    os_number_display() {
      if (this.data.os_number) {
        return `OS #${String(this.data.os_number).padStart(4, "0")}`;
      }
      return "SEQUENCIAL AUTOMÁTICO";
    },
  },
  created() {
    this.$http.get("/prefectures").then((r) => {
      this.prefectures = r.data;
      if (this.$store.getters.getUserData.table_key === "prefectures") {
        this.data.prefecture_id = this.$store.getters.getUserData.table_id;
        this.loadProviders();
        this.getVehiclesByPrefecture();
      }
    });

    if (this.id) {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get(`/parts-and-services/os/${this.id}`).then((r) => {
        const itemData = r.data[0];
        this.data = {
          prefecture_id: itemData.prefecture_id,
          provider_id: itemData.provider_id,
          vehicle_id: itemData.vehicle_id,
          os_number: itemData.os_number,
          os_date: this.convertDate(itemData.os_date),
          os_responsible: itemData.os_responsible || "",
          os_status: itemData.os_status || "ABERTO",
          os_total: itemData.os_total || "0,00",
        };
        this.itens = JSON.parse(itemData.os_items || "[]");
        this.loadProviders();
        this.getVehiclesByPrefecture();
        this.loadProvidersItem();
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    } else {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      this.data.os_date = `${dd}/${mm}/${yyyy}`;
    }
  },
  methods: {
    convertDate(date) {
      if (date) {
        return date.substring(0, 10).split("-").reverse().join("/");
      }
      return "";
    },
    loadProviders() {
      this.$http
        .get(`/parts-and-services/providers/prefecture/${this.data.prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    getVehiclesByPrefecture() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/vehicles/prefectures/${this.data.prefecture_id}`)
        .then((v) => {
          this.vehicles = v.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    loadProvidersItem() {
      this.$http
        .get(`/parts-and-services/nf/providers/${this.data.provider_id}`)
        .then((r) => {
          this.providersItem = r.data;
        });
    },
    addFieldParts() {
      this.itens.push({
        peca_id: null,
        preco: "",
        quantidade: "",
        total: "",
        isServico: false,
      });
    },
    addFieldServices() {
      this.itens.push({
        servico: "",
        preco: "",
        isServico: true,
      });
    },
    removeField(index) {
      this.itens.splice(index, 1);
      this.onChangeTotal();
    },
    onChangeParts(index) {
      const preco = parseFloat(this.itens[index].preco.split(".").join("").replace(",", "."));
      const qtd = parseFloat(this.itens[index].quantidade) || 0;
      const total = preco * qtd;
      if (!isNaN(total)) {
        this.itens[index].total = total.toFixed(2).toString().replace(".", ",");
      }
      this.onChangeTotal();
    },
    onChangeTotal() {
      let total = 0;
      for (let i = 0; i < this.itens.length; i++) {
        const item = this.itens[i];
        if (item.isServico && item.preco) {
          total += parseFloat(item.preco.split(".").join("").replace(",", "."));
        } else if (!item.isServico && item.total) {
          total += parseFloat(item.total.split(".").join("").replace(",", "."));
        }
      }
      this.data.os_total = total.toFixed(2).toString().replace(".", ",");
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atenção: um ou mais campos precisam ser preenchidos!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      this.data.itens = this.itens;

      const prepareValue = (val) => {
        if (!val || val === "") return "0.00";
        return val.toString().split(".").join("").replace(",", ".");
      };

      this.data.os_total = prepareValue(this.data.os_total);

      this.$store.dispatch("setLoader", true);

      const request = this.id
        ? this.$http.put(`/parts-and-services/os/${this.id}`, this.data)
        : this.$http.post("/parts-and-services/os", this.data);

      request
        .then((r) => {
          this.snack.show = true;
          if (r.data.result || r.data.success) {
            this.snack.message = r.data.result || r.data.success;
            this.snack.color = "success";
            setTimeout(() => {
              this.$router.push("/parts-and-services/os");
            }, 1500);
          } else {
            this.snack.message = r.data.error;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          }
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select-readonly ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select-small ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 38px !important;
}

.custom-select-small-readonly ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 38px !important;
}

.custom-select ::v-deep input, 
.custom-select-readonly ::v-deep input,
.custom-select-small ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select-small ::v-deep input {
  font-size: 12px !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

/* Animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
